import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAlert } from '../components/AlertContext';
import { validarRut, validarEmail, formatearRut } from '../utils/validaciones';
import Sidebar from '../components/Sidebar';
import Skeleton from '../components/Skeleton';
import Pagination from '../components/Pagination';
import './Dashboard.css';

const Usuarios = () => {
  const navigate = useNavigate();
  const { toast, confirm } = useAlert();
  const [usuarios, setUsuarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') || 'usuarios';
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [roles, setRoles] = useState([]);
  const [permisosGlobales, setPermisosGlobales] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [roleUsers, setRoleUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isUpdatingPerms, setIsUpdatingPerms] = useState(false);

  const [profanityList, setProfanityList] = useState('');
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showWords, setShowWords] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    rut: '',
    rol: '',
    rol_id: '',
    departamento: '',
    password: '123'
  });
  const [formErrors, setFormErrors] = useState({});
  const [areasList, setAreasList] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;

  useEffect(() => {
    const role = currentUser.rol?.toLowerCase();
    const permisos = currentUser.permisos || [];
    // Superadmin siempre entra, otros necesitan MODULO_ADMIN
    if (role !== 'superadmin' && !permisos.includes('MODULO_ADMIN')) {
      navigate('/dashboard');
    }
    fetchUsuarios();
    fetchProfanityList();
    fetchRolesAndPerms();
    fetchAreas();
  }, [navigate]);

  const fetchAreas = async () => {
    try {
      const resp = await fetch(`${apiBase}/api/areas`, { credentials: 'include' });
      if (resp.ok) setAreasList(await resp.json());
    } catch (err) { console.error('Error cargando áreas:', err); }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, filtroRol]);

  const fetchRolesAndPerms = async () => {
    try {
      const respRoles = await fetch(`${apiBase}/api/admin/roles`, { credentials: 'include' });
      const respPerms = await fetch(`${apiBase}/api/admin/permisos`, { credentials: 'include' });
      if (respRoles.ok) {
        const rolesData = await respRoles.json();
        setRoles(rolesData);
        if (rolesData.length > 0 && !selectedRole) {
          setSelectedRole(rolesData[0]);
          setRolePermissions(rolesData[0].permisos);
          setFormData(prev => ({ ...prev, rol_id: rolesData[0].id, rol: rolesData[0].slug }));
        }
      }
      if (respPerms.ok) setPermisosGlobales(await respPerms.json());
    } catch (err) { console.error('Error fetching RBAC data:', err); }
  };

  useEffect(() => {
    if (selectedRole) {
      fetchRoleUsers(selectedRole.id);
      setIsEditingUser(false);
      setSelectedUser(null);
      setRolePermissions(selectedRole.permisos || []);
    }
  }, [selectedRole]);

  const fetchRoleUsers = async (roleId) => {
    try {
      const resp = await fetch(`${apiBase}/api/admin/roles/${roleId}/usuarios`, { credentials: 'include' });
      if (resp.ok) setRoleUsers(await resp.json());
    } catch (err) { console.error('Error fetching role users:', err); }
  };

  const handleUpdateRolePerms = async () => {
    if (!selectedRole) return;
    setIsUpdatingPerms(true);
    try {
      const url = isEditingUser 
        ? `${apiBase}/api/admin/usuarios/${selectedUser.uid}/permisos`
        : `${apiBase}/api/admin/roles/${selectedRole.id}/permisos`;
      
      const resp = await fetch(url, { credentials: 'include', 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permisos: rolePermissions })
      });
      if (resp.ok) {
        toast.success(isEditingUser 
          ? `Permisos para ${selectedUser.nombre} actualizados con éxito.` 
          : 'Permisos de rol actualizados con éxito.');
        if (isEditingUser) fetchRoleUsers(selectedRole.id);
        else fetchRolesAndPerms();
      }
    } catch (err) { console.error(err); }
    finally { setIsUpdatingPerms(false); }
  };

  const togglePermission = (slug) => {
    setRolePermissions(prev =>
      prev.includes(slug) ? prev.filter(p => p !== slug) : [...prev, slug]
    );
  };

  const fetchUsuarios = async () => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/usuarios`, { credentials: 'include' });
      if (resp.ok) setUsuarios(await resp.json());
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfanityList = async () => {
    try {
      const resp = await fetch(`${apiBase}/api/config/profanity`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setProfanityList(data.words);
      }
    } catch (err) { console.error('Error fetching profanity list:', err); }
  };



  const handleSaveConfig = async () => {
    setIsSavingConfig(true);
    setSaveStatus(null);
    try {
      const resp = await fetch(`${apiBase}/api/config/profanity`, { credentials: 'include', 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ words: profanityList })
      });
      if (resp.ok) setSaveStatus('success');
      else setSaveStatus('error');
    } catch (err) {
      setSaveStatus('error');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleBlockManual = async (uid, nombre) => {
    if (uid === currentUser.uid) return toast.error("No puedes bloquear tu propia cuenta.");
    confirm(
      "Suspender Usuario",
      `¿Estás seguro de suspender indefinidamente a: ${nombre}?`,
      async () => {
        try {
          const resp = await fetch(`${apiBase}/api/usuarios/${uid}/bloquear_permanente`, { credentials: 'include',  method: 'PATCH' });
          if (resp.ok) {
            fetchUsuarios();
            toast.success('Usuario bloqueado con éxito.');
          } else {
            const err = await resp.json();
            toast.error(`Error: ${err.error || 'No se pudo bloquear el usuario.'}`);
          }
        } catch (err) {
          console.error(err);
          toast.error('Error de conexión al intentar bloquear.');
        }
      },
      { confirmText: 'Suspender', type: 'danger' }
    );
  };

  const handleUnlock = async (uid, nombre) => {
    confirm(
      "Desbloquear Usuario",
      `¿Estás seguro de desbloquear a: ${nombre}?`,
      async () => {
        try {
          const resp = await fetch(`${apiBase}/api/usuarios/${uid}/desbloquear`, { credentials: 'include',  method: 'POST' });
          if (resp.ok) {
            fetchUsuarios();
            toast.success('Usuario desbloqueado con éxito.');
          }
        } catch (err) {
          console.error(err);
          toast.error('Error de conexión');
        }
      },
      { confirmText: 'Desbloquear', type: 'info' }
    );
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'rut') {
      setFormData(prev => ({ ...prev, rut: formatearRut(value) }));
    }
    if (name === 'rol_id') {
      const selected = roles.find(r => r.id === parseInt(value));
      if (selected) {
        setFormData(prev => ({ ...prev, rol_id: value, rol: selected.slug }));
      }
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const errors = {};
    // Validaciones
    if (!formData.nombre) errors.nombre = 'El nombre es obligatorio.';
    if (!formData.email || !validarEmail(formData.email)) errors.email = 'Email inválido.';
    if (!formData.rut || !validarRut(formData.rut)) errors.rut = 'RUT inválido.';
    if (!formData.departamento) {
      errors.departamento = 'Debes seleccionar un Departamento/Área.';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const url = isEditingUser ? `${apiBase}/api/usuarios/${selectedUser?.uid}` : `${apiBase}/api/usuarios`;
      const method = isEditingUser ? 'PATCH' : 'POST';

      const resp = await fetch(url, { credentials: 'include', 
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (resp.ok) {
        setIsModalOpen(false);
        setIsEditingUser(false);
        setSelectedUser(null);
        setFormData({
          nombre: '',
          email: '',
          rut: '',
          rol: roles.length > 0 ? roles[0].slug : '',
          rol_id: roles.length > 0 ? roles[0].id : '',
          departamento: '',
          password: '123'
        });
        fetchUsuarios();
        toast.success(isEditingUser ? 'Usuario actualizado con éxito.' : 'Usuario creado con éxito. Contraseña inicial enviada por correo.');
        fetchUsuarios();
      } else {
        const error = await resp.json();
        toast.error('Error: ' + error.error);
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const handleEditClick = (u) => {
    setFormData({
      nombre: u.nombre,
      email: u.email,
      rut: u.rut,
      rol: u.rol,
      rol_id: u.rol_id,
      departamento: u.departamento,
      password: '***'
    });
    setSelectedUser(u);
    setIsEditingUser(true);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const filteredUsuarios = usuarios.filter(u => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (u.nombre || '').toLowerCase().includes(term) || (u.rut || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term);
    const matchesRol = !filtroRol || u.rol_id === parseInt(filtroRol);
    
    if (activeTab === 'bloqueados') {
        const isBlocked = u.intentos_fallidos >= 999 || (u.bloqueado_hasta && new Date(u.bloqueado_hasta) > new Date());
        return matchesSearch && matchesRol && isBlocked;
    }
    return matchesSearch && matchesRol;
  });

  const totalPages = Math.ceil(filteredUsuarios.length / itemsPerPage);
  const currentItems = filteredUsuarios.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <main className="main-content">
        <header className="content-header">
          <div className="header-title">
            <h1><i className="fa-solid fa-shield-halved" style={{ marginRight: '14px', background: 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}></i>Seguridad y Roles</h1>
            <p style={{ color: 'var(--p-slate)', fontSize: '14px', marginTop: '4px', fontWeight: '500' }}>Gestión centralizada de accesos, permisos y políticas de seguridad del sistema.</p>
          </div>
        </header>

        {/* Stats Widgets */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
          <div className="widget-card" style={{ borderBottomColor: 'var(--p-indigo)' }}>
            <h3>Usuarios Activos</h3>
            <p className="big-number">{usuarios.filter(u => !(u.intentos_fallidos >= 999 || (u.bloqueado_hasta && new Date(u.bloqueado_hasta) > new Date()))).length}</p>
          </div>
          <div className="widget-card" style={{ borderBottomColor: 'var(--p-blue)' }}>
            <h3>Roles del Sistema</h3>
            <p className="big-number">{roles.length}</p>
          </div>
          <div className="widget-card" style={{ borderBottomColor: 'var(--p-rose)' }}>
            <h3>Cuentas Suspendidas</h3>
            <p className="big-number">{usuarios.filter(u => u.intentos_fallidos >= 999 || (u.bloqueado_hasta && new Date(u.bloqueado_hasta) > new Date())).length}</p>
          </div>
          <div className="widget-card" style={{ borderBottomColor: 'var(--p-emerald)' }}>
            <h3>Permisos Definidos</h3>
            <p className="big-number">{permisosGlobales.length}</p>
          </div>
        </div>



        {(activeTab === 'usuarios' || activeTab === 'bloqueados') ? (
          <section className="datatable-section animate-fade-in" style={{ marginTop: '0' }}>
            <div className="datatable-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', gap: '15px' }}>
              <h2 style={{ margin: 0, color: 'var(--p-navy)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className={`fa-solid ${activeTab === 'bloqueados' ? 'fa-user-lock' : 'fa-users-gear'}`} style={{ color: activeTab === 'bloqueados' ? 'var(--p-rose)' : 'var(--p-indigo)', fontSize: '18px' }}></i>
                {activeTab === 'bloqueados' ? 'Cuentas Suspendidas' : 'Directorio de Cuentas'}
              </h2>
              
              {activeTab === 'usuarios' && (
                <div className="header-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div className="search-bar" style={{ margin: 0 }}>
                    <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', marginLeft: '15px', marginTop: '16px', color: 'var(--p-slate)' }}></i>
                    <input
                      type="text"
                      placeholder="Buscar por nombre, RUT o email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ paddingLeft: '40px', width: '260px', height: '45px' }}
                    />
                  </div>
                  <select
                    className="select-premium"
                    value={filtroRol}
                    onChange={(e) => setFiltroRol(e.target.value)}
                    style={{ width: '170px', height: '45px' }}
                  >
                    <option value="">Todos los Roles</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                  </select>
                  <button className="btn-primary" onClick={() => { setFormData({ nombre: '', email: '', rut: '', rol_id: roles[0]?.id, departamento: '', password: '123' }); setFormErrors({}); setIsEditingUser(false); setSelectedUser(null); setIsModalOpen(true); }} style={{ height: '45px', margin: 0 }}>
                    <i className="fa-solid fa-user-plus"></i> Nuevo Usuario
                  </button>
                </div>
              )}
            </div>

            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>RUT</th>
                    <th>Nivel de Acceso</th>
                    <th style={{ textAlign: 'center' }}>Estado</th>
                    <th style={{ textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i} className="skeleton-row-tr">
                        <td><Skeleton width="200px" /></td>
                        <td><Skeleton width="110px" /></td>
                        <td><Skeleton width="120px" /></td>
                        <td style={{ textAlign: 'center' }}><Skeleton width="100px" /></td>
                        <td style={{ textAlign: 'right' }}><Skeleton width="70px" /></td>
                      </tr>
                    ))
                  ) : currentItems.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '50px 20px' }}>
                      <i className="fa-solid fa-users-slash" style={{ fontSize: '36px', color: 'var(--p-slate)', opacity: 0.3, display: 'block', marginBottom: '12px' }}></i>
                      <span style={{ color: 'var(--p-slate)', fontSize: '14px' }}>No hay usuarios en esta categoría.</span>
                    </td></tr>
                  ) : currentItems.map(u => {
                        const isBlocked = u.intentos_fallidos >= 999 || (u.bloqueado_hasta && new Date(u.bloqueado_hasta) > new Date());
                        const initials = (u.nombre || 'U').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                        const rolLower = (u.rol || '').toLowerCase();
                        const rolColor = rolLower === 'admin' || rolLower === 'superadmin' ? 'var(--p-rose)' : rolLower === 'supervisor' ? 'var(--p-indigo)' : rolLower === 'jefatura' ? 'var(--p-amber)' : rolLower === 'operador' ? 'var(--p-blue)' : 'var(--p-slate)';
                        return (
                          <tr key={u.uid}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: `linear-gradient(135deg, ${rolColor}, ${rolColor}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: '800', flexShrink: 0, boxShadow: `0 4px 10px ${rolColor}33` }}>
                              {initials}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <strong style={{ color: 'var(--p-navy)', fontSize: '14px' }}>{u.nombre}</strong>
                              <span style={{ fontSize: '11px', color: 'var(--p-slate)' }}>
                                <i className="fa-solid fa-envelope" style={{ fontSize: '9px', marginRight: '4px' }}></i>{u.email}
                                <span style={{ margin: '0 6px', opacity: 0.4 }}>•</span>
                                <i className="fa-solid fa-building" style={{ fontSize: '9px', marginRight: '4px' }}></i>{u.departamento || 'General'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td style={{ color: 'var(--p-slate)', fontSize: '13px', fontFamily: 'monospace', letterSpacing: '0.02em' }}>{u.rut || 'N/A'}</td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', background: `${rolColor}15`, color: rolColor, border: `1px solid ${rolColor}25` }}>
                            <i className={`fa-solid ${rolLower === 'admin' || rolLower === 'superadmin' ? 'fa-crown' : rolLower === 'supervisor' ? 'fa-chart-line' : rolLower === 'jefatura' ? 'fa-user-tie' : rolLower === 'operador' ? 'fa-screwdriver-wrench' : 'fa-eye'}`} style={{ fontSize: '10px' }}></i>
                            {u.rol_nombre || u.rol.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {isBlocked ? (
                            <span className="badge" style={{ background: 'rgba(244, 63, 94, 0.1)', color: 'var(--p-rose)', border: '1px solid rgba(244, 63, 94, 0.2)', fontSize: '11px' }}>
                              <i className="fa-solid fa-lock" style={{ marginRight: '4px' }}></i> Bloqueado
                            </span>
                          ) : (
                            <span className="badge" style={{ background: u.intentos_fallidos > 0 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)', color: u.intentos_fallidos > 0 ? 'var(--p-amber)' : 'var(--p-emerald)', border: `1px solid ${u.intentos_fallidos > 0 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`, fontSize: '11px' }}>
                              <i className={`fa-solid ${u.intentos_fallidos > 0 ? 'fa-triangle-exclamation' : 'fa-circle-check'}`} style={{ marginRight: '4px', fontSize: '10px' }}></i>
                              {u.intentos_fallidos > 0 ? `${u.intentos_fallidos}/5 Fallos` : 'Activo'}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="table-actions">
                            <button className="action-button-glass" onClick={() => handleEditClick(u)} title="Editar Datos" style={{ color: 'var(--p-blue)' }}>
                              <i className="fa-solid fa-pen-to-square"></i>
                            </button>
                            {isBlocked ? (
                              <button className="action-button-glass" style={{ color: 'var(--p-emerald)' }} onClick={() => handleUnlock(u.uid, u.nombre)} title="Desbloquear Usuario">
                                <i className="fa-solid fa-unlock-keyhole"></i>
                              </button>
                            ) : (
                              <button className="action-button-glass action-delete" onClick={() => handleBlockManual(u.uid, u.nombre)} title="Suspender Usuario">
                                <i className="fa-solid fa-user-lock"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </section>
        ) : activeTab === 'config' ? (
          <section className="chart-panel-premium animate-fade-in" style={{ maxWidth: '800px', marginTop: '0' }}>
            <div className="panel-header" style={{ marginBottom: '20px' }}>
              <i className="fa-solid fa-shield-virus" style={{ color: 'var(--p-indigo)', fontSize: '20px' }}></i>
              <h3 style={{ margin: 0, color: 'var(--p-navy)' }}>Filtro de Contenido y Lenguaje</h3>
            </div>
            <p style={{ color: 'var(--p-slate)', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
              Define las palabras prohibidas que el sistema bloqueará automáticamente en las descripciones de equipos, comentarios e historiales para mantener un estándar profesional.
            </p>
            <div className="security-controls" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="quick-add" style={{ padding: '20px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--p-slate)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '12px' }}>Añadir Nueva Palabra al Filtro</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    id="new-word-input"
                    placeholder="Escribe una palabra y presiona Enter..."
                    style={{ flex: 1, background: 'var(--input-bg)', border: '1px solid var(--glass-border)', color: 'var(--p-navy)' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = e.target.value.trim().toLowerCase();
                        if (val) {
                          const currentWords = profanityList.split(',').map(w => w.trim()).filter(w => w);
                          if (!currentWords.includes(val)) {
                            setProfanityList(prev => prev ? `${prev}, ${val}` : val);
                            toast.info(`"${val}" añadida. Haz clic en "Actualizar Filtros" para guardar los cambios.`);
                            e.target.value = '';
                          } else {
                            toast.warning('La palabra ya existe en la lista.');
                          }
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ height: '45px', padding: '0 20px' }}
                    onClick={() => {
                      const input = document.getElementById('new-word-input');
                      const val = input.value.trim().toLowerCase();
                      if (val) {
                        const currentWords = profanityList.split(',').map(w => w.trim()).filter(w => w);
                        if (!currentWords.includes(val)) {
                          setProfanityList(prev => prev ? `${prev}, ${val}` : val);
                          toast.info(`"${val}" añadida. Haz clic en "Actualizar Filtros" para guardar.`);
                          input.value = '';
                        } else {
                          toast.warning('La palabra ya existe en la lista.');
                        }
                      }
                    }}
                  >
                    <i className="fa-solid fa-plus"></i> Añadir
                  </button>
                </div>
              </div>

              <div className="premium-field" style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ margin: 0 }}>LISTA COMPLETA (EDICIÓN MANUAL)</label>
                  <button
                    type="button"
                    onClick={() => setShowWords(!showWords)}
                    className="btn-secondary"
                    style={{ padding: '4px 12px', fontSize: '11px', height: 'auto', background: showWords ? 'rgba(244, 63, 94, 0.1)' : 'rgba(56, 189, 248, 0.1)', color: showWords ? 'var(--p-rose)' : 'var(--p-blue)', border: 'none' }}
                  >
                    <i className={`fa-solid ${showWords ? 'fa-eye-slash' : 'fa-eye'}`}></i> {showWords ? 'Ocultar lista completa' : 'Ver/Editar lista completa'}
                  </button>
                </div>

                {showWords ? (
                  <textarea
                    value={profanityList}
                    onChange={(e) => setProfanityList(e.target.value)}
                    style={{ minHeight: '180px', lineHeight: '1.6', background: 'var(--input-bg)', border: '1px solid var(--glass-border)', color: 'var(--p-navy)' }}
                    placeholder="palabra1, palabra2, ..."
                    className="animate-fade-in"
                  />
                ) : (
                  <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--sidebar-accent)', border: '1px dashed var(--glass-border)', borderRadius: '8px', color: 'var(--p-slate)', fontSize: '13px' }}>
                    <i className="fa-solid fa-lock" style={{ marginRight: '8px' }}></i> La lista completa está oculta. Usa el campo superior para añadir palabras rápidamente.
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button className="btn-primary" onClick={handleSaveConfig} disabled={isSavingConfig} style={{ height: '45px' }}>
                <i className="fa-solid fa-save"></i> {isSavingConfig ? 'Guardando...' : 'Actualizar Filtros'}
              </button>
              {saveStatus === 'success' && <span style={{ color: 'var(--p-emerald)', fontSize: '13px', fontWeight: '600' }}><i className="fa-solid fa-check-circle"></i> Configuración guardada.</span>}
              {saveStatus === 'error' && <span style={{ color: 'var(--p-rose)', fontSize: '13px', fontWeight: '600' }}><i className="fa-solid fa-circle-xmark"></i> Error al guardar.</span>}
            </div>
          </section>
        ) : (
          <section className="rbac-panel animate-fade-in">
            <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '30px' }}>
              <div className="sidebar-selection-panel" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="roles-list-panel" style={{ background: 'var(--card-bg)', borderRadius: '15px', border: '1px solid var(--glass-border)', padding: '20px' }}>
                  <h3 style={{ fontSize: '11px', color: 'var(--p-slate)', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '1px' }}>1. Selecciona un Perfil</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {roles.map(r => (
                      <button
                        key={r.id}
                        className={`role-select-btn ${selectedRole?.id === r.id ? 'active' : ''}`}
                        onClick={() => setSelectedRole(r)}
                        style={{
                          padding: '10px 15px',
                          textAlign: 'left',
                          background: selectedRole?.id === r.id ? 'var(--sidebar-accent)' : 'transparent',
                          border: '1px solid',
                          borderColor: selectedRole?.id === r.id ? 'var(--p-blue)' : 'var(--glass-border)',
                          color: selectedRole?.id === r.id ? 'var(--p-indigo)' : 'var(--p-slate)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '13px'
                        }}
                      >
                        <span>{r.nombre}</span>
                        {selectedRole?.id === r.id && <i className="fa-solid fa-check" style={{ fontSize: '10px' }}></i>}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedRole && (
                  <div className="users-role-panel animate-fade-in" style={{ background: 'var(--card-bg)', borderRadius: '15px', border: '1px solid var(--glass-border)', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h3 style={{ fontSize: '11px', color: 'var(--p-slate)', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>2. Usuarios con este Perfil</h3>
                      {!isEditingUser && <span className="p-badge" style={{ fontSize: '9px', padding: '2px 6px' }}>Editando Rol</span>}
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto', paddingRight: '5px' }}>
                      <button
                        className={`role-select-btn ${!isEditingUser ? 'active' : ''}`}
                        onClick={() => {
                          setIsEditingUser(false);
                          setSelectedUser(null);
                          setRolePermissions(selectedRole.permisos || []);
                        }}
                        style={{
                          padding: '10px 15px',
                          textAlign: 'left',
                          background: !isEditingUser ? 'var(--sidebar-accent)' : 'transparent',
                          border: '1px dashed',
                          borderColor: !isEditingUser ? 'var(--p-blue)' : 'var(--glass-border)',
                          color: !isEditingUser ? 'var(--p-indigo)' : 'var(--p-slate)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: !isEditingUser ? '600' : '400'
                        }}
                      >
                        <i className="fa-solid fa-users-gear" style={{ marginRight: '8px' }}></i> Configuración de Rol Base
                      </button>

                      <div style={{ height: '1px', background: 'var(--glass-border)', margin: '5px 0' }}></div>

                      {roleUsers.length === 0 ? (
                        <p style={{ fontSize: '12px', color: 'var(--p-slate)', textAlign: 'center', padding: '10px' }}>No hay usuarios asignados a este perfil.</p>
                      ) : roleUsers.map(u => (
                        <button
                          key={u.uid}
                          className={`user-select-btn ${selectedUser?.uid === u.uid ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedUser(u);
                            setIsEditingUser(true);
                            // Cargar permisos custom o los del rol si no tiene
                            try {
                              const customPerms = u.permisos_custom ? JSON.parse(u.permisos_custom) : selectedRole.permisos;
                              setRolePermissions(customPerms || []);
                            } catch (e) { setRolePermissions(selectedRole.permisos || []); }
                          }}
                          style={{
                            padding: '10px 15px',
                            textAlign: 'left',
                            background: selectedUser?.uid === u.uid ? 'var(--sidebar-accent)' : 'transparent',
                            border: '1px solid',
                            borderColor: selectedUser?.uid === u.uid ? 'var(--p-emerald)' : 'var(--glass-border)',
                            color: selectedUser?.uid === u.uid ? 'var(--p-emerald)' : 'var(--p-slate)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px'
                          }}
                        >
                          <span style={{ fontSize: '13px', fontWeight: selectedUser?.uid === u.uid ? '600' : '400' }}>{u.nombre}</span>
                          <span style={{ fontSize: '10px', opacity: 0.7 }}>{u.rut}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="permissions-matrix-panel" style={{ background: 'var(--card-bg)', borderRadius: '15px', border: '1px solid var(--glass-border)', padding: '30px' }}>
                {!selectedRole ? (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--p-slate)' }}>
                    <i className="fa-solid fa-key" style={{ fontSize: '48px', marginBottom: '20px', opacity: 0.2 }}></i>
                    <p>Selecciona un perfil a la izquierda para gestionar sus accesos.</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                          <span className={`badge status-${isEditingUser ? 'operativo' : 'baja'}`} style={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            {isEditingUser ? 'Edición Individual' : 'Edición de Perfil'}
                          </span>
                          <h2 style={{ margin: 0, color: 'var(--p-navy)' }}>
                            {isEditingUser ? `Accesos de: ${selectedUser.nombre}` : `Capacidades de: ${selectedRole.nombre}`}
                          </h2>
                        </div>
                        <p style={{ margin: 0, color: 'var(--p-slate)', fontSize: '13px' }}>
                          {isEditingUser 
                            ? "Estos permisos reemplazan totalmente la configuración del rol para este usuario."
                            : "Define los permisos por defecto para todos los usuarios con este perfil."}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {isEditingUser && (
                          <button
                            className="btn-secondary"
                            onClick={() => {
                            confirm(
                              "Restablecer Permisos",
                              "¿Restablecer a permisos por defecto del rol?",
                              () => {
                                setRolePermissions([...(selectedRole.permisos || [])]);
                                toast.info('Permisos restablecidos a los valores por defecto del rol.');
                              },
                              { confirmText: 'Restablecer', type: 'warning' }
                            );
                            }}
                            style={{ height: '42px' }}
                          >
                            <i className="fa-solid fa-undo"></i> Reset
                          </button>
                        )}
                        <button
                          className="btn-primary"
                          onClick={handleUpdateRolePerms}
                          disabled={isUpdatingPerms}
                          style={{ height: '42px', padding: '0 25px' }}
                        >
                          <i className="fa-solid fa-save"></i> {isUpdatingPerms ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                      </div>
                    </div>

                    <div className="permissions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                      {Array.from(new Set(permisosGlobales.map(p => p.categoria))).map(cat => (
                        <div key={cat} className="permission-category-box" style={{ background: 'var(--sidebar-accent)', padding: '20px', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                          <h4 style={{ margin: '0 0 15px', fontSize: '11px', color: 'var(--p-blue)', textTransform: 'uppercase', letterSpacing: '1px' }}>{cat}</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {permisosGlobales.filter(p => p.categoria === cat).map(p => (
                              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--p-navy)', fontSize: '14px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={rolePermissions.includes(p.slug)}
                                  onChange={() => togglePermission(p.slug)}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span>{p.nombre}</span>
                                  <small style={{ fontSize: '11px', color: 'var(--p-slate)' }}>{p.slug}</small>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal animate-fade-in" style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ marginBottom: '20px' }}>
              <h2 style={{ color: 'var(--p-navy)', margin: 0 }}>{isEditingUser ? 'Editar Datos de Usuario' : 'Registrar Nuevo Usuario'}</h2>
            </div>
            <form onSubmit={handleCreateUser} className="crud-form">
              <div className="premium-field">
                <label><i className="fa-solid fa-user" style={{ color: 'var(--p-indigo)' }}></i> Nombre Completo</label>
                <input required name="nombre" value={formData.nombre} onChange={handleFormChange} placeholder="Ej: Juan Pérez" />
                {formErrors.nombre && <small className="error">{formErrors.nombre}</small>}
              </div>
              <div className="premium-field">
                <label><i className="fa-solid fa-envelope" style={{ color: 'var(--p-blue)' }}></i> Email Municipal (Login)</label>
                <input required type="email" name="email" value={formData.email} onChange={handleFormChange} placeholder="email@municipalidad.cl" />
                {formErrors.email && <small className="error">{formErrors.email}</small>}
              </div>

              {!isEditingUser && (
                <div className="premium-field">
                  <label><i className="fa-solid fa-id-card" style={{ color: 'var(--p-amber)' }}></i> RUT Asociado</label>
                  <input required name="rut" value={formData.rut} onChange={handleFormChange} placeholder="12.345.678-9" />
                  {formErrors.rut && <small className="error">{formErrors.rut}</small>}
                </div>
              )}

              <div className="premium-field">
                <label><i className="fa-solid fa-shield-halved" style={{ color: 'var(--p-rose)' }}></i> Nivel de Privilegios y Rol</label>
                <select className="select-premium" name="rol_id" value={formData.rol_id} onChange={handleFormChange}>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.nombre}</option>
                  ))}
                </select>
              </div>
              
              <div className="premium-field">
                <label><i className="fa-solid fa-building" style={{ color: 'var(--p-emerald)' }}></i> Departamento / Área a Cargo</label>
                <select 
                  name="departamento" 
                  className="select-premium"
                  value={formData.departamento} 
                  onChange={handleFormChange}
                  style={formErrors.departamento ? { borderColor: '#ef4444' } : {}}
                >
                  <option value="">— Seleccionar área obligatoria —</option>
                  {areasList.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
                {formErrors.departamento && <small style={{ color: '#ef4444', display: 'block', marginTop: '4px' }}>{formErrors.departamento}</small>}
                <p style={{ fontSize: '10px', color: 'var(--p-slate)', marginTop: '4px' }}>
                  Esencial para redirigir solicitudes automáticamente a Jefaturas de área.
                </p>
              </div>

              {!isEditingUser && (
                <div style={{ padding: '12px', background: 'var(--sidebar-accent)', borderRadius: '8px', border: '1px solid var(--glass-border)', marginTop: '15px' }}>
                  <p style={{ fontSize: '11px', color: 'var(--p-blue)', margin: 0, fontWeight: '500' }}>
                    <i className="fa-solid fa-circle-info"></i> La clave inicial será el <strong>RUT sin puntos, sin guion y sin el último dígito (DV)</strong>.
                  </p>
                </div>
              )}

              <div className="modal-footer-actions" style={{ marginTop: '25px' }}>
                <button type="button" className="btn-secondary" onClick={() => { setIsModalOpen(false); setIsEditingUser(false); setSelectedUser(null); }}>Cancelar</button>
                <button type="submit" className="btn-primary">
                  <i className="fa-solid fa-save"></i> {isEditingUser ? 'Guardar Cambios' : 'Crear Acceso'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
